import React from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../../auth";
import { loadTeacherWorkload } from "../../services/teacherWorkloadService";

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [q, setQ] = React.useState("");
  const [schoolYear, setSchoolYear] = React.useState("");
  const [sections, setSections] = React.useState([]);
  const [classes, setClasses] = React.useState([]);
  const [loadError, setLoadError] = React.useState("");

  React.useEffect(() => {
    (async () => {
      const campusLC = String(user?.campus || "gapan").toLowerCase();
      try {
        setLoadError("");
        const res = await loadTeacherWorkload({ user, campus: campusLC });
        const wl = res.data || {};
        setSchoolYear(res.schoolYear || "");
        setSections(Array.isArray(wl.advisories) ? wl.advisories : []);
        const cls = Array.isArray(wl.classes) ? wl.classes.filter(c =>
          String(c.grade || "").trim() || String(c.section || "").trim() || String(c.subject || "").trim() || String(c.gradebookUrl || "").trim()
        ) : [];
        setClasses(cls);
      } catch (e) {
        const msg = String(e?.message || "Failed to load teacher workload.");
        setLoadError(msg);
        setSections([]);
        setClasses([]);
      }
    })();
  }, [user?.uid, user?.email, user?.campus]);

  function goStudents() {
    const text = q.trim();
    if (!text) return;
    try { localStorage.setItem("globalStudentSearch", text); } catch (e) { void e; }
    navigate(`/teacher/classes`);
  }

  const name = user?.displayName || user?.email || "Teacher";
  const campus = (user?.campus || "").toString();

  return (
    <div className="container py-4">
      <h3 className="mb-3">Teacher Portal</h3>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-2">Welcome, {name}</h5>
              <div className="text-muted">Campus: {campus || "—"}</div>
              {schoolYear && <div className="text-muted">School Year: {schoolYear}</div>}
              <div className="mt-3 d-flex gap-2">
                <button className="btn btn-primary" onClick={() => navigate("/teacher/attendance")}>
                  Take Attendance
                </button>
                <button className="btn btn-outline-primary" onClick={() => navigate("/teacher/classes")}>
                  View Class Program
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h6 className="fw-bold mb-2">Quick Student Search</h6>
              <div className="input-group">
                <input className="form-control" placeholder="Search by name or ID" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === "Enter") goStudents(); }} />
                <button className="btn btn-outline-secondary" onClick={goStudents}>Search</button>
              </div>
              <div className="form-text">Press Enter to open SIS → Students with the filter applied.</div>
            </div>
          </div>
        </div>
      </div>
      <div className="row g-3 mt-1">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h6 className="fw-bold">Shortcuts</h6>
              <div className="d-flex flex-wrap gap-2">
                <button className="btn btn-outline-secondary" onClick={() => navigate("/teacher/attendance")}>Attendance</button>
                <button className="btn btn-outline-secondary" onClick={() => navigate("/teacher/classes")}>Class Program</button>
                <button className="btn btn-outline-secondary" onClick={() => navigate("/teacher/gradebooks")}>Gradebooks</button>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h6 className="fw-bold">My Sections</h6>
              {loadError && <div className="text-danger small mb-2">{loadError}</div>}
              {sections.length === 0 ? <div className="text-muted">No assigned advisories found</div> : (
                <ul className="mb-0">
                  {sections.map((s,i) => (
                    <li key={i}>
                      {s.grade} — {s.section}{" "}
                      <button className="btn btn-sm btn-outline-primary ms-2" onClick={() => navigate(`/teacher/attendance`)}>
                        Take Attendance
                      </button>
                      <button className="btn btn-sm btn-outline-secondary ms-2" onClick={() => navigate(`/teacher/class-list?campus=${encodeURIComponent(String(user?.campus || "gapan").toLowerCase())}&grade=${encodeURIComponent(s.grade)}&section=${encodeURIComponent(s.section)}`)}>
                        Class List
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row g-3 mt-1">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h6 className="fw-bold">Assigned Classes</h6>
              {classes.length === 0 ? (
                <div className="text-muted">No assigned classes yet</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead className="table-light">
                      <tr><th>Grade</th><th>Section</th><th>Subject</th><th>Schedule</th><th></th></tr>
                    </thead>
                    <tbody>
                      {classes.map((c, idx) => (
                        <tr key={idx}>
                          <td>{c.grade || ""}</td>
                          <td>{c.section || ""}</td>
                          <td>{c.subject || ""}</td>
                          <td>{c.schedule || ""}</td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => window.open(String(c.gradebookUrl || c.gradebookLink || c.link || ""), "_blank", "noopener,noreferrer")} disabled={!(c.gradebookUrl || c.gradebookLink || c.link)}>
                              Gradebook
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
